import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import CharacterCard from '@/components/CharacterCard';
import BookList from '@/components/BookList';
import ConfirmDialog from '@/components/ConfirmDialog';
import SeriesCoverPicker from '@/components/SeriesCoverPicker';
import CharacterPortraitPicker from '@/components/CharacterPortraitPicker';
import { useSeries } from '@/contexts/SeriesContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  BookOpen,
  Users,
  Globe,
  Palette,
  Plus,
  Settings,
  Loader2,
  Trash2,
  X,
  Clock,
  CheckCircle,
  PauseCircle,
  Sparkles,
  Upload,
  Wand2,
  ImageIcon,
  Camera,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const SeriesDetailPage = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const coverInputRef = useRef(null);
  const {
    currentSeries,
    seriesBooks,
    loading,
    error,
    fetchSeries,
    fetchUserSeries,
    updateSeries,
    deleteSeries,
    addCharacter,
    updateCharacter,
    deleteCharacter,
    clearError,
    seriesLimit
  } = useSeries();

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [premiumStatusLoaded, setPremiumStatusLoaded] = useState(false);

  // Fetch user series to get premium status on mount
  useEffect(() => {
    const loadPremiumStatus = async () => {
      await fetchUserSeries();
      setPremiumStatusLoaded(true);
    };
    loadPremiumStatus();
  }, [fetchUserSeries]);

  // Redirect non-premium users to the series list (which shows the preview)
  // Wait for premium status to be loaded before checking
  useEffect(() => {
    if (premiumStatusLoaded && seriesLimit && !seriesLimit.isPremium) {
      toast.error('Series is a premium feature', {
        description: 'Upgrade to Premium to access your series'
      });
      navigate('/series');
    }
  }, [premiumStatusLoaded, seriesLimit, navigate]);
  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Settings form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('active');
  const [isPublic, setIsPublic] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Character form state
  const [charName, setCharName] = useState('');
  const [charRole, setCharRole] = useState('supporting');
  const [charDescription, setCharDescription] = useState('');
  const [charTraits, setCharTraits] = useState(['']);
  const [savingCharacter, setSavingCharacter] = useState(false);

  // Cover image state
  const [coverLoading, setCoverLoading] = useState(false);
  const [localCoverUrl, setLocalCoverUrl] = useState(null);

  // Character portrait loading state (track multiple character IDs simultaneously)
  const [portraitLoadingIds, setPortraitLoadingIds] = useState(new Set());

  // Delete confirmation dialogs
  const [showDeleteSeriesDialog, setShowDeleteSeriesDialog] = useState(false);
  const [showDeleteCharacterDialog, setShowDeleteCharacterDialog] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  const [deletingCharacter, setDeletingCharacter] = useState(false);

  // Cover regeneration confirmation
  const [showRegenerateCoverDialog, setShowRegenerateCoverDialog] = useState(false);

  // Characters without portraits warning for cover generation
  const [showCharactersFirstDialog, setShowCharactersFirstDialog] = useState(false);

  // Enlarged portrait modal
  const [enlargedPortrait, setEnlargedPortrait] = useState(null);

  // Cover picker modal
  const [showCoverPicker, setShowCoverPicker] = useState(false);

  // Portrait picker modal
  const [showPortraitPicker, setShowPortraitPicker] = useState(false);
  const [selectedCharacterForPortrait, setSelectedCharacterForPortrait] = useState(null);

  // Active jobs for this series (generating books)
  const [activeJobs, setActiveJobs] = useState([]);
  const location = useLocation();

  // Bulk visibility state
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  // Fetch active jobs for this series
  const fetchActiveJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/v1/jobs/mine?include_completed=false`, {
        withCredentials: true
      });
      // Filter jobs for this series
      const seriesJobs = (response.data.jobs || []).filter(
        job => job.series_id === seriesId && ['queued', 'processing'].includes(job.status)
      );
      setActiveJobs(seriesJobs);

      // If a job completed, refresh the series to get the new book
      const prevJobIds = activeJobs.map(j => j.id);
      const currentJobIds = seriesJobs.map(j => j.id);
      const completedJobs = prevJobIds.filter(id => !currentJobIds.includes(id));
      if (completedJobs.length > 0) {
        fetchSeries(seriesId);
      }
    } catch (error) {
      console.error('Failed to fetch active jobs:', error);
    }
  }, [seriesId, activeJobs, fetchSeries]);

  // Poll for job updates
  useEffect(() => {
    fetchActiveJobs();

    // Poll every 3 seconds if there are active jobs or we just navigated here
    const interval = setInterval(() => {
      fetchActiveJobs();
    }, 3000);

    return () => clearInterval(interval);
  }, [seriesId]); // Don't include fetchActiveJobs to avoid infinite loop

  // If we came from creating a book, show the job immediately
  useEffect(() => {
    if (location.state?.newJobId) {
      fetchActiveJobs();
      // Clear the state so refreshing doesn't trigger this again
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Cover image handlers
  const handleUploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/v1/series/${seriesId}/cover/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setLocalCoverUrl(response.data.cover_url);
      toast.success('Cover uploaded!');
      fetchSeries(seriesId);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload cover');
    } finally {
      setCoverLoading(false);
    }
  };

  const handleGenerateCover = async () => {
    setCoverLoading(true);
    setShowRegenerateCoverDialog(false);

    try {
      const response = await axios.post(
        `${API}/v1/series/${seriesId}/cover/generate`,
        {},
        { withCredentials: true }
      );

      setLocalCoverUrl(response.data.cover_url);
      toast.success('Cover generated!');
      fetchSeries(seriesId);
    } catch (err) {
      console.error('Generate error:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate cover');
    } finally {
      setCoverLoading(false);
    }
  };

  // Check if any characters are missing portraits
  const getCharactersWithoutPortraits = () => {
    if (!currentSeries?.characters?.length) return [];
    return currentSeries.characters.filter(c => !c.portrait_url);
  };

  // Check if cover exists and show confirmation, or generate directly
  const handleGenerateCoverClick = () => {
    const coverUrl = localCoverUrl || currentSeries?.cover_image_url;
    const missingPortraits = getCharactersWithoutPortraits();

    // If characters exist but some don't have portraits, show warning
    if (currentSeries?.characters?.length > 0 && missingPortraits.length > 0) {
      setShowCharactersFirstDialog(true);
      return;
    }

    if (coverUrl) {
      // Cover exists - show confirmation
      setShowRegenerateCoverDialog(true);
    } else {
      // No cover - generate directly
      handleGenerateCover();
    }
  };

  // Generate cover anyway (after warning)
  const handleGenerateCoverAnyway = () => {
    setShowCharactersFirstDialog(false);
    const coverUrl = localCoverUrl || currentSeries?.cover_image_url;
    if (coverUrl) {
      setShowRegenerateCoverDialog(true);
    } else {
      handleGenerateCover();
    }
  };

  // Character portrait handlers
  const handleUploadPortrait = async (characterId, file) => {
    // Add to loading set (allows multiple simultaneous uploads)
    setPortraitLoadingIds(prev => new Set([...prev, characterId]));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('art_style', currentSeries.art_style || 'storybook');

      await axios.post(
        `${API}/v1/series/${seriesId}/characters/${characterId}/portrait/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success('Portrait transformed!');
      fetchSeries(seriesId);
    } catch (err) {
      console.error('Portrait upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to transform portrait');
    } finally {
      // Remove from loading set
      setPortraitLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(characterId);
        return next;
      });
    }
  };

  const handleGeneratePortrait = async (characterId) => {
    // Add to loading set (allows multiple simultaneous generations)
    setPortraitLoadingIds(prev => new Set([...prev, characterId]));

    try {
      await axios.post(
        `${API}/v1/series/${seriesId}/characters/${characterId}/portrait/generate`,
        {},
        { withCredentials: true }
      );

      toast.success('Portrait generated!');
      fetchSeries(seriesId);
    } catch (err) {
      console.error('Portrait generate error:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate portrait');
    } finally {
      // Remove from loading set
      setPortraitLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(characterId);
        return next;
      });
    }
  };

  // Handler for cover picker changes
  const handleCoverChange = (newCoverUrl, coverImages, activeIndex) => {
    setLocalCoverUrl(newCoverUrl);
    // Refresh series data to get updated state
    fetchSeries(seriesId);
  };

  // Handler for portrait picker changes
  const handlePortraitChange = (characterId, newPortraitUrl, portraits, activeIndex) => {
    // Refresh series data to get updated state
    fetchSeries(seriesId);
  };

  // Open portrait picker for a character
  const openPortraitPicker = (character) => {
    setSelectedCharacterForPortrait(character);
    setShowPortraitPicker(true);
  };

  // Open cover picker - if cover exists, show picker; otherwise generate first cover
  const handleCoverClick = () => {
    const coverUrl = localCoverUrl || currentSeries?.cover_image_url;
    if (coverUrl) {
      // Cover exists - show picker
      setShowCoverPicker(true);
    } else {
      // No cover yet - check if we need to warn about missing portraits
      const missingPortraits = getCharactersWithoutPortraits();
      if (currentSeries?.characters?.length > 0 && missingPortraits.length > 0) {
        setShowCharactersFirstDialog(true);
      } else {
        // Generate first cover directly
        handleGenerateCover();
      }
    }
  };

  useEffect(() => {
    fetchSeries(seriesId);
  }, [seriesId, fetchSeries]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  useEffect(() => {
    if (currentSeries) {
      setTitle(currentSeries.title || '');
      setDescription(currentSeries.description || '');
      setStatus(currentSeries.status || 'active');
      setIsPublic(currentSeries.is_public || false);
    }
  }, [currentSeries]);

  const handleOpenSettings = () => {
    setTitle(currentSeries?.title || '');
    setDescription(currentSeries?.description || '');
    setStatus(currentSeries?.status || 'active');
    setIsPublic(currentSeries?.is_public || false);
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateSeries(seriesId, {
        title: title.trim(),
        description: description.trim() || null,
        status,
        is_public: isPublic
      });
      toast.success('Series updated!');
      setShowSettingsModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update series');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteSeries = async () => {
    setDeleting(true);
    try {
      await deleteSeries(seriesId);
      toast.success('Series deleted');
      navigate('/series');
    } catch (err) {
      toast.error(err.message || 'Failed to delete series');
      setDeleting(false);
      setShowDeleteSeriesDialog(false);
    }
  };

  const resetCharacterForm = () => {
    setCharName('');
    setCharRole('supporting');
    setCharDescription('');
    setCharTraits(['']);
    setEditingCharacter(null);
  };

  const handleOpenAddCharacter = () => {
    resetCharacterForm();
    setShowAddCharacterModal(true);
  };

  const handleEditCharacter = (character) => {
    setCharName(character.name);
    setCharRole(character.role);
    setCharDescription(character.description || '');
    setCharTraits(character.traits?.length > 0 ? character.traits : ['']);
    setEditingCharacter(character);
    setShowAddCharacterModal(true);
  };

  const handleSaveCharacter = async () => {
    if (!charName.trim()) {
      toast.error('Character name is required');
      return;
    }

    setSavingCharacter(true);
    try {
      const charData = {
        name: charName.trim(),
        role: charRole,
        description: charDescription.trim() || '',
        traits: charTraits.filter(t => t.trim())
      };

      if (editingCharacter) {
        await updateCharacter(seriesId, editingCharacter.id, charData);
        toast.success('Character updated!');
      } else {
        await addCharacter(seriesId, charData);
        toast.success('Character added!');
      }

      setShowAddCharacterModal(false);
      resetCharacterForm();
    } catch (err) {
      toast.error(err.message || 'Failed to save character');
    } finally {
      setSavingCharacter(false);
    }
  };

  const handleDeleteCharacter = async () => {
    if (!characterToDelete) return;

    setDeletingCharacter(true);
    try {
      await deleteCharacter(seriesId, characterToDelete.id);
      toast.success('Character removed');
      setShowDeleteCharacterDialog(false);
      setCharacterToDelete(null);
    } catch (err) {
      toast.error(err.message || 'Failed to remove character');
    } finally {
      setDeletingCharacter(false);
    }
  };

  const openDeleteCharacterDialog = (character) => {
    setCharacterToDelete(character);
    setShowDeleteCharacterDialog(true);
  };

  const addTrait = () => {
    if (charTraits.length < 5) {
      setCharTraits([...charTraits, '']);
    }
  };

  const updateTrait = (index, value) => {
    const updated = [...charTraits];
    updated[index] = value;
    setCharTraits(updated);
  };

  const removeTrait = (index) => {
    if (charTraits.length > 1) {
      setCharTraits(charTraits.filter((_, i) => i !== index));
    }
  };

  // Bulk visibility update for all books
  const handleBulkVisibility = async (makePublic) => {
    if (!seriesBooks?.length) return;

    setUpdatingVisibility(true);
    const action = makePublic ? 'public' : 'private';

    try {
      // Update all books in parallel
      const updatePromises = seriesBooks.map(book => {
        const storyId = book.story_id || book.id;
        return axios.patch(`${API}/stories/${storyId}`, {
          is_public: makePublic
        }, { withCredentials: true });
      });

      await Promise.all(updatePromises);
      toast.success(`All books are now ${action}`);
      fetchSeries(seriesId); // Refresh to get updated book data
    } catch (error) {
      console.error('Failed to update visibility:', error);
      toast.error(`Failed to make books ${action}`);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const statusConfig = {
    active: { icon: Clock, color: '#22C55E', bg: '#DCFCE7', label: 'Active' },
    completed: { icon: CheckCircle, color: '#3B82F6', bg: '#DBEAFE', label: 'Completed' },
    hiatus: { icon: PauseCircle, color: '#F59E0B', bg: '#FEF3C7', label: 'On Hiatus' }
  };

  if (loading && !currentSeries) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-60px)] md:min-h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading series...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!currentSeries) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-60px)] md:min-h-screen">
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
            <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
              Series Not Found
            </p>
            <button
              onClick={() => navigate('/series')}
              className="text-sm hover:underline"
              style={{ color: 'var(--st-primary)' }}
            >
              Back to Series
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const currentStatus = statusConfig[currentSeries.status] || statusConfig.active;
  const StatusIcon = currentStatus.icon;
  const coverUrl = localCoverUrl || currentSeries.cover_image_url;

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-8 md:px-6">

          {/* Header Card */}
          <div className="rounded-2xl shadow-sm p-5 mb-6" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate('/series')}
                className="p-2 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors mt-0.5"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    {currentSeries.title}
                  </h1>
                  <span
                    className="px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5"
                    style={{ background: currentStatus.bg, color: currentStatus.color }}
                  >
                    <StatusIcon className="w-3.5 h-3.5" />
                    {currentStatus.label}
                  </span>
                </div>
                {currentSeries.description && (
                  <p className="text-sm mt-2 leading-relaxed max-w-3xl" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    {currentSeries.description}
                  </p>
                )}
              </div>

              <button
                onClick={handleOpenSettings}
                className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                style={{ border: '1px solid var(--st-border-light)' }}
              >
                <Settings className="w-5 h-5" style={{ color: 'var(--st-text-muted)' }} />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Column - Cover & Create */}
            <div className="lg:col-span-1 space-y-4">
              {/* Hidden file input */}
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleUploadCover}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
              />

              {/* Cover Card */}
              <div className="rounded-2xl shadow-sm overflow-hidden w-fit mx-auto" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <div
                  className="relative aspect-[4/5] w-[280px] group cursor-pointer"
                  style={{ background: 'linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                  onClick={() => {
                    if (!coverLoading && coverUrl && currentSeries.status !== 'completed') {
                      setShowCoverPicker(true);
                    }
                  }}
                >
                  {coverUrl ? (
                    <img
                      src={getImageUrl(coverUrl)}
                      alt={currentSeries.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center p-4 text-center cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCoverClick();
                      }}
                    >
                      <BookOpen className="w-12 h-12 mb-3" style={{ color: '#FFCDB2' }} />
                      <p className="text-sm font-medium" style={{ color: '#C4A389', fontFamily: 'Quicksand, sans-serif' }}>
                        Add a cover image
                      </p>
                    </div>
                  )}

                  {/* Loading Overlay */}
                  {coverLoading && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                      <Loader2 className="w-10 h-10 text-white animate-spin mb-3" />
                      <p className="text-white text-sm font-medium">Generating...</p>
                    </div>
                  )}

                  {/* Hover overlay with edit hint - Only show if series is not completed and has cover */}
                  {!coverLoading && coverUrl && currentSeries.status !== 'completed' && (
                    <div className="absolute inset-0 bg-black/0 sm:group-hover:bg-black/40 transition-all flex items-center justify-center">
                      <div className="flex flex-col items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <div className="p-3 rounded-xl bg-white/90 shadow-lg">
                          <ImageIcon className="w-5 h-5" style={{ color: '#818CF8' }} />
                        </div>
                        <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                          Click to change cover
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Empty state action buttons - Only show if series is not completed */}
                  {!coverLoading && !coverUrl && currentSeries.status !== 'completed' && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          coverInputRef.current?.click();
                        }}
                        className="p-2.5 rounded-xl bg-white/90 hover:bg-white transition-colors shadow-md"
                        title="Upload cover"
                      >
                        <Upload className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCoverClick();
                        }}
                        className="p-2.5 rounded-xl bg-white/90 hover:bg-white transition-colors shadow-md"
                        title="Generate with AI"
                      >
                        <Wand2 className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Create Book Button - only show if series is not completed */}
              {currentSeries.status !== 'completed' && (
                <button
                  onClick={() => navigate(`/series/${seriesId}/create-book`)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-white text-sm transition-all hover:scale-[1.02] hover:shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                    fontFamily: 'Quicksand, sans-serif',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)'
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Create Book {(currentSeries.book_count || 0) + activeJobs.length + 1}
                </button>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg p-3 text-center" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  <BookOpen className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--st-primary)' }} />
                  <p className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    {currentSeries.book_count || 0}
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: '#9CA3AF' }}>
                    Books
                  </p>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  <Users className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--st-primary)' }} />
                  <p className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    {currentSeries.characters?.length || 0}
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: '#9CA3AF' }}>
                    Characters
                  </p>
                </div>
              </div>

              {/* Series Info - Art Style & World */}
              <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                {/* Art Style */}
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--st-bg-elevated)' }}>
                    <Palette className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs" style={{ color: '#9CA3AF' }}>Art Style</p>
                    <p className="text-sm font-medium capitalize truncate" style={{ color: 'var(--st-text-heading)' }}>
                      {currentSeries.art_style || 'Storybook'}
                    </p>
                  </div>
                </div>

                {/* World */}
                {currentSeries.world?.name && (
                  <div className="flex items-start gap-2 pt-2" style={{ borderTop: '1px solid var(--st-border-light)' }}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--st-bg-elevated)' }}>
                      <Globe className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs" style={{ color: '#9CA3AF' }}>World</p>
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--st-text-heading)' }}>
                        {currentSeries.world.name}
                      </p>
                      {currentSeries.world.description && (
                        <p className="text-xs leading-relaxed mt-1 line-clamp-2" style={{ color: 'var(--st-text-muted)' }}>
                          {currentSeries.world.description}
                        </p>
                      )}
                      {currentSeries.world.locations?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {currentSeries.world.locations.slice(0, 3).map((loc, i) => (
                            <span
                              key={i}
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-muted)' }}
                            >
                              {loc.length > 20 ? loc.substring(0, 20) + '...' : loc}
                            </span>
                          ))}
                          {currentSeries.world.locations.length > 3 && (
                            <span className="text-xs px-1.5 py-0.5" style={{ color: '#9CA3AF' }}>
                              +{currentSeries.world.locations.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Characters & Books */}
            <div className="lg:col-span-2 space-y-6">

              {/* Characters Section */}
              <div className="rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--st-border-light)' }}>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                    <h2 className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      Characters
                    </h2>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)' }}>
                      {currentSeries.characters?.length || 0}
                    </span>
                  </div>
                  {currentSeries.status !== 'completed' && (
                    <button
                      onClick={handleOpenAddCharacter}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                      style={{ background: '#EEF2FF', color: '#6366F1' }}
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  )}
                </div>

                <div className="p-5 pb-0">
                  {currentSeries.characters?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {currentSeries.characters.map((character) => (
                        <CharacterCard
                          key={character.id}
                          character={character}
                          onEdit={currentSeries.status !== 'completed' ? () => handleEditCharacter(character) : undefined}
                          onDelete={currentSeries.status !== 'completed' ? () => openDeleteCharacterDialog(character) : undefined}
                          onUploadPortrait={currentSeries.status !== 'completed' ? handleUploadPortrait : undefined}
                          onGeneratePortrait={currentSeries.status !== 'completed' ? handleGeneratePortrait : undefined}
                          onViewPortrait={(url, name) => setEnlargedPortrait({ url, name })}
                          onPickPortrait={currentSeries.status !== 'completed' ? openPortraitPicker : undefined}
                          portraitLoading={portraitLoadingIds.has(character.id)}
                          hasExistingBooks={seriesBooks?.length > 0}
                          viewOnly={currentSeries.status === 'completed'}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <div
                        className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                        style={{ background: 'var(--st-bg-elevated)' }}
                      >
                        <Users className="w-8 h-8" style={{ color: 'var(--st-primary)' }} />
                      </div>
                      <p className="font-semibold mb-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                        No characters yet
                      </p>
                      <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: '#9CA3AF' }}>
                        Add characters to maintain consistency across your series
                      </p>
                      {currentSeries.status !== 'completed' && (
                        <button
                          onClick={handleOpenAddCharacter}
                          className="px-4 py-2 rounded-xl font-semibold text-sm transition-all hover:scale-105"
                          style={{ background: '#EEF2FF', color: '#6366F1' }}
                        >
                          Add First Character
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Tip for portrait upload */}
                {currentSeries.characters?.length > 0 && (
                  <div className="px-5 py-4">
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
                      <Camera className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--st-primary)' }} />
                      <p className="text-xs" style={{ color: 'var(--st-text-muted)' }}>
                        <span className="font-semibold">Tip:</span> Click a portrait to view it enlarged, or use the camera button to upload or generate a new one
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Books Section */}
              <div className="rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--st-border-light)' }}>
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                    <h2 className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      Books in Series
                    </h2>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)' }}>
                      {seriesBooks?.length || 0}
                    </span>
                  </div>

                  {/* Bulk visibility toggle - only show if there are books */}
                  {seriesBooks?.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          disabled={updatingVisibility}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80 disabled:opacity-50"
                          style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-muted)' }}
                        >
                          {updatingVisibility ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                          Visibility
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={() => handleBulkVisibility(true)}
                          className="cursor-pointer"
                        >
                          <Globe className="w-4 h-4 mr-2" style={{ color: '#22C55E' }} />
                          <span>Make all public</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleBulkVisibility(false)}
                          className="cursor-pointer"
                        >
                          <Lock className="w-4 h-4 mr-2" style={{ color: '#EF4444' }} />
                          <span>Make all private</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                <div className="p-5">
                  <BookList
                    books={seriesBooks}
                    seriesId={seriesId}
                    activeJobs={activeJobs}
                    onCreateBook={() => navigate(`/series/${seriesId}/create-book`)}
                    isSeriesCompleted={currentSeries.status === 'completed'}
                    onBookUpdate={() => {
                      // Refresh the series to get updated book data
                      fetchSeries(seriesId);
                    }}
                    onBookDelete={() => {
                      // Refresh the series to update the book list
                      fetchSeries(seriesId);
                    }}
                    onJobCancel={async (jobId) => {
                      try {
                        await axios.delete(`${API}/v1/jobs/${jobId}`, { withCredentials: true });
                        toast.success('Book creation cancelled, credit refunded');
                        setActiveJobs(prev => prev.filter(j => j.id !== jobId));
                      } catch (error) {
                        toast.error('Failed to cancel');
                      }
                    }}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSettingsModal(false)} />
          <div className="relative rounded-2xl p-6 w-full max-w-md shadow-2xl" style={{ background: 'var(--st-bg-surface)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Series Settings
              </h3>
              <button onClick={() => setShowSettingsModal(false)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <X className="w-5 h-5" style={{ color: '#9CA3AF' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-12 rounded-xl"
                  style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                />
              </div>

              <div>
                <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="rounded-xl min-h-[100px] resize-none"
                  style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                />
              </div>

              <div>
                <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-12 rounded-xl" style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="hiatus">On Hiatus</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Visibility Toggle */}
              <div>
                <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--st-text-heading)' }}>Visibility</Label>
                <div
                  onClick={() => setIsPublic(!isPublic)}
                  className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-sm ${isPublic ? 'dark:bg-green-900/30' : ''}`}
                  style={{
                    border: '1px solid var(--st-border-light)',
                    background: isPublic ? '#DCFCE7' : 'var(--st-bg-muted)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: isPublic ? '#22C55E' : '#9CA3AF' }}
                    >
                      {isPublic ? (
                        <Eye className="w-5 h-5 text-white" />
                      ) : (
                        <EyeOff className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${isPublic ? 'dark:text-green-200' : ''}`} style={{ color: isPublic ? '#15803D' : 'var(--st-text-heading)' }}>
                        {isPublic ? 'Public' : 'Private'}
                      </p>
                      <p className={`text-xs ${isPublic ? 'dark:text-green-300' : ''}`} style={{ color: isPublic ? '#166534' : 'var(--st-text-muted)' }}>
                        {isPublic
                          ? 'Anyone can view this series'
                          : 'Only you can see this series'
                        }
                      </p>
                    </div>
                  </div>
                  <div
                    className="w-12 h-7 rounded-full p-1 transition-colors"
                    style={{ background: isPublic ? '#22C55E' : '#D1D5DB' }}
                  >
                    <div
                      className="w-5 h-5 rounded-full bg-white transition-transform"
                      style={{ transform: isPublic ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowSettingsModal(false);
                  setShowDeleteSeriesDialog(true);
                }}
                className="p-3 rounded-xl font-semibold text-red-500 border border-red-200 hover:bg-red-50 dark:hover:bg-red-950/30 dark:border-red-800 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-colors hover:opacity-80"
                style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}
              >
                {savingSettings ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Character Modal */}
      {showAddCharacterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAddCharacterModal(false)} />
          <div className="relative rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" style={{ background: 'var(--st-bg-surface)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                {editingCharacter ? 'Edit Character' : 'Add Character'}
              </h3>
              <button onClick={() => setShowAddCharacterModal(false)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <X className="w-5 h-5" style={{ color: '#9CA3AF' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--st-text-muted)' }}>Name *</Label>
                  <Input
                    value={charName}
                    onChange={(e) => setCharName(e.target.value)}
                    placeholder="Character name"
                    className="h-11 rounded-xl"
                    style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--st-text-muted)' }}>Role</Label>
                  <Select value={charRole} onValueChange={setCharRole}>
                    <SelectTrigger className="h-11 rounded-xl" style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="protagonist">Protagonist</SelectItem>
                      <SelectItem value="supporting">Supporting</SelectItem>
                      <SelectItem value="antagonist">Antagonist</SelectItem>
                      <SelectItem value="mentor">Mentor</SelectItem>
                      <SelectItem value="sidekick">Sidekick</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--st-text-muted)' }}>Description</Label>
                <Textarea
                  value={charDescription}
                  onChange={(e) => setCharDescription(e.target.value)}
                  placeholder="Physical appearance, personality, backstory..."
                  className="rounded-xl min-h-[80px] resize-none"
                  style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--st-text-muted)' }}>Traits</Label>
                <div className="flex flex-wrap gap-2">
                  {charTraits.map((trait, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <Input
                        value={trait}
                        onChange={(e) => updateTrait(index, e.target.value)}
                        placeholder="e.g., brave"
                        className="h-9 text-sm w-28 rounded-lg"
                        style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-input)' }}
                      />
                      {charTraits.length > 1 && (
                        <button onClick={() => removeTrait(index)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {charTraits.length < 5 && (
                    <button
                      onClick={addTrait}
                      className="h-9 px-3 rounded-lg text-sm font-medium transition-colors"
                      style={{ background: 'var(--st-bg-elevated)', color: 'var(--st-primary)' }}
                    >
                      + Add
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddCharacterModal(false)}
                className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-colors hover:opacity-80"
                style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCharacter}
                disabled={savingCharacter || !charName.trim()}
                className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}
              >
                {savingCharacter ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : editingCharacter ? 'Update' : 'Add Character'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Series Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteSeriesDialog}
        onOpenChange={setShowDeleteSeriesDialog}
        title="Delete Series?"
        description="This will permanently delete this series. Stories in this series will be kept but will no longer be associated with the series."
        confirmText="Delete Series"
        onConfirm={handleDeleteSeries}
        loading={deleting}
        variant="danger"
      />

      {/* Delete Character Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteCharacterDialog}
        onOpenChange={(open) => {
          setShowDeleteCharacterDialog(open);
          if (!open) setCharacterToDelete(null);
        }}
        title={`Remove ${characterToDelete?.name || 'Character'}?`}
        description={
          seriesBooks?.length > 0
            ? "This character will be removed from the series. Existing books featuring this character won't be affected."
            : "This character will be permanently removed from the series."
        }
        confirmText="Remove"
        onConfirm={handleDeleteCharacter}
        loading={deletingCharacter}
        variant="danger"
      />

      {/* Regenerate Cover Confirmation Dialog */}
      <ConfirmDialog
        open={showRegenerateCoverDialog}
        onOpenChange={setShowRegenerateCoverDialog}
        title="Regenerate Cover?"
        description="This will replace your existing cover image with a new AI-generated one. The new cover will use your character portraits for consistency."
        confirmText="Regenerate"
        cancelText="Keep Current"
        onConfirm={handleGenerateCover}
        loading={coverLoading}
        variant="warning"
      />

      {/* Characters First Warning Dialog */}
      <ConfirmDialog
        open={showCharactersFirstDialog}
        onOpenChange={setShowCharactersFirstDialog}
        title="Create Character Portraits First?"
        description={
          <>
            <span className="block mb-2">
              {getCharactersWithoutPortraits().length === 1
                ? `Your character "${getCharactersWithoutPortraits()[0]?.name}" doesn't have a portrait yet.`
                : `${getCharactersWithoutPortraits().length} characters don't have portraits yet: ${getCharactersWithoutPortraits().map(c => c.name).join(', ')}.`
              }
            </span>
            <span className="block text-sm" style={{ color: 'var(--st-text-muted)' }}>
              For the best cover that matches your characters, create their portraits first. You can always regenerate the cover later.
            </span>
          </>
        }
        confirmText="Generate Anyway"
        cancelText="Create Portraits First"
        onConfirm={handleGenerateCoverAnyway}
        loading={coverLoading}
        variant="warning"
      />

      {/* Enlarged Portrait Modal */}
      {enlargedPortrait && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setEnlargedPortrait(null)}
        >
          <div className="relative max-w-2xl max-h-[80vh]">
            <button
              onClick={() => setEnlargedPortrait(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="text-center mb-4">
              <h3 className="text-white text-lg font-bold" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                {enlargedPortrait.name}
              </h3>
            </div>
            <img
              src={enlargedPortrait.url}
              alt={enlargedPortrait.name}
              className="max-w-full max-h-[70vh] rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Series Cover Picker Modal */}
      {currentSeries && (
        <SeriesCoverPicker
          series={currentSeries}
          isOpen={showCoverPicker}
          onClose={() => setShowCoverPicker(false)}
          onCoverChange={handleCoverChange}
        />
      )}

      {/* Character Portrait Picker Modal */}
      {selectedCharacterForPortrait && (
        <CharacterPortraitPicker
          seriesId={seriesId}
          character={selectedCharacterForPortrait}
          isOpen={showPortraitPicker}
          onClose={() => {
            setShowPortraitPicker(false);
            setSelectedCharacterForPortrait(null);
          }}
          onPortraitChange={handlePortraitChange}
        />
      )}
    </PageLayout>
  );
};

export default SeriesDetailPage;
